// Route destinations and the navigations that carry a visitor to one: the
// paths whose slices have not landed yet, and the sign-in destination that
// every path back to login shares.
//
// A leaf module on purpose: the legacy route files need these constants and
// nothing else about routing policy, so they do not reach through
// route-guards.ts (whose subject is *who* may pass) to get them.

import { redirect } from "@tanstack/react-router";

import type { ShareDenial } from "@/api/contracts/share-public";
import type { SpecType } from "@/api/contracts/specifications";

// Paths whose slices landed after the code that names them.
// `redirect({ to })` is type-checked against the route tree, so a path the
// tree did not know yet could not travel that way.
//
// A path graduates *out* of this module the moment its route lands: an on-tree
// destination travels as a compiler-checked `to`, which is a stronger guard
// than any shared string constant, so the constant stops earning its keep.
// `/logout` graduated this way.
//
// A path whose slice has not landed resolves the way any unmatched path does:
// the authed catch-all, and so the branded 404 once the visitor is signed in.
// That is the accepted posture for this build — the observable destination is
// the contract term, dev/uat are the only environments this reaches, and
// everything lands before cutover.
//
// The communities list's route has landed (#750), and the landing guards now
// send visitors there with a typed `to` — but like `ITEM_CATALOG_PATH` below
// this constant stays: it reaches `Link` through the header's nav model, whose
// destinations are widened to `string` (see `components/app-shell/nav.ts`),
// and the `/dashboard` bookmark resolves through it.
export const COMMUNITIES_PATH = "/communities";
export const SPECIFICATIONS_BASE_PATH = "/specifications/base";
// The Item Catalog section's route has landed (#249), so this is no longer an
// off-tree placeholder — but like `ADMIN_PATH` below it has not graduated out
// of this module, and for the same reason: it reaches `Link` through the
// header's nav model, whose destinations are widened to `string` for its
// off-tree neighbours (see `components/app-shell/nav.ts`), so there is no
// compiler-checked `to` for it to become.
export const ITEM_CATALOG_PATH = "/items";

// The Admin section's route has landed (#225), so this one is no longer an
// off-tree placeholder — but it has not graduated out of this module the way
// `/logout` did, because the reason for graduating does not apply: it reaches
// `Link` through the header's nav model, whose destinations are widened to
// `string` for its off-tree neighbours (see `components/app-shell/nav.ts`), so
// there is no compiler-checked `to` for it to become. The admin route's own
// tabs *do* use a typed `to="/admin"`.
export const ADMIN_PATH = "/admin";

/**
 * One item's details page.
 *
 * On the tree since #251 (`routes/_authed/_chrome/items/$itemId.tsx`), but the
 * return stays widened to `string`: the path is built from an id at runtime, so
 * a template literal handed to `Link`'s `to` is still `string` and not the
 * literal route pattern the router's types check against. Callers that want the
 * checked form pass `to="/items/$itemId"` with `params` themselves.
 *
 * It is a function rather than a constant because the id is the whole point,
 * and every view of the list links to the same place: three spellings of
 * `/items/${id}` across card, list, and table is three chances to disagree.
 */
export function itemDetailPath(itemId: number): string {
  return `${ITEM_CATALOG_PATH}/${itemId}`;
}

/**
 * The same page as an absolute link — what both item menus' Copy Link puts on
 * the clipboard (the details route's, and the Item Search dialog's, #798).
 * Browser-only: it reads the page's origin.
 */
export function itemDetailUrl(itemId: number): string {
  return `${window.location.origin}${itemDetailPath(itemId)}`;
}

/**
 * One community's details page — the communities list's ID and Name links
 * (`CommunityLinkCell`, `communities-data-table.tsx:20-26`): the internal id,
 * then the full id the donor's URL carries beside it.
 *
 * A string for the reason `itemDetailPath` is one. The details route (#751)
 * reads only the first segment; the full id rides along as the donor's URL
 * carries it.
 */
export function communityDetailPath(communityId: number, externalFullId: string): string {
  return `${COMMUNITIES_PATH}/${communityId}/${externalFullId}`;
}

/**
 * One spec version's details page — the Spec list's Name, Core Specification,
 * Based On and Linked Specs links (`navigation.ts`, `getSpecificationVersionPath`).
 *
 * A string for the reason `itemDetailPath` is one: built from ids at runtime,
 * so there is no literal route pattern for the router to check; callers
 * wanting the checked form pass `to` with `params` themselves.
 */
export function specDetailPath(specId: number, versionId: number): string {
  return `/specifications/${specId}/versions/${versionId}`;
}

/**
 * A draft's edit route — the pencil's target when the row has a draft
 * (`spec-edit-button/actions.ts:24-30`). Its guard (#694) lets an editor on
 * an open draft through to the editor (#806).
 */
export function specEditPath(specId: number, versionId: number): string {
  return `${specDetailPath(specId, versionId)}/edit`;
}

/**
 * Redirects to an off-tree `destination`, replacing the current history entry.
 *
 * Two invariants live here rather than in each route file, so neither can be
 * forgotten one route at a time:
 *
 * - `href`, not `to` — the destination is off the route tree (above). A
 *   *relative* href stays an ordinary in-app navigation once the app is
 *   running; only an absolute URL makes the router infer a document load.
 *
 *   On a cold request — a typed URL or a bookmark, which is most of this
 *   contract's traffic — the redirect is thrown during SSR, and TanStack Start
 *   serialises it into a real HTTP 307 with a `Location` header. That is
 *   inherent to expressing a redirect at route level rather than a property of
 *   `href`: the alternative, rendering HTML that navigates on mount, costs a
 *   flash and a second round trip. Route-level is the mechanism the spec asked
 *   for; the 307 is what SSR makes of it.
 * - `replace` — the path being left must not sit in history, or pressing Back
 *   from the destination re-enters the redirect and bounces the visitor
 *   forward again.
 */
export function redirectOffTree(destination: string): never {
  throw redirect({ href: destination, replace: true });
}

/** `beforeLoad` for a legacy path whose whole job is to resolve elsewhere. */
export function legacyRedirectTo(destination: string): () => never {
  return () => redirectOffTree(destination);
}

/**
 * The sign-in destination, carrying where to come back to.
 *
 * Three places send a visitor to login and every one of them owes them the way
 * back: the `_authed` gate on navigation, the session-expired overlay in place,
 * and the error component's backstop. The return-to is the whole contract, so
 * it is stated once — a site that forgot the search param would strand the
 * visitor on the landing page with no sign that they had asked for anywhere
 * else.
 *
 * Returns the navigation options rather than performing the navigation: the
 * gate throws them through `redirect()`, the other two hand them to
 * `router.navigate()`.
 */
export function loginWithReturnTo(href: string) {
  return { to: "/login", search: { redirect: href } } as const;
}

/**
 * One tab of the Spec list — the details page's breadcrumb root. A `string`
 * like `specDetailPath`, for the callers that hand a router `Link` a built
 * path.
 */
export function specListPath(specType: SpecType): string {
  return `/specifications/${specType}`;
}

/**
 * A spec or version id from a URL segment, or `null` when the segment is not
 * one — so `/specifications/abc/versions/1` answers Page not found without a
 * read, rather than failing the server function's validator.
 *
 * **Leading zeros are the id** (#554 D6): the donor reads its ids with
 * `Number(param)` (`SpecDetailsIsland.tsx:53-54`, `VersionSelect.tsx:21-22`),
 * and the legacy resolvers hand the raw segment to DRF's integer lookup, so
 * `…/versions/023347` opens version 23347 and the URL stays as typed. The rest
 * of what `Number` would take — `0`, a sign, `1e3`, `0x10`, padding spaces —
 * stays refused.
 */
export function parseRouteId(segment: string): number | null {
  if (!/^\d+$/.test(segment)) return null;
  const id = Number(segment);
  return id > 0 && Number.isSafeInteger(id) ? id : null;
}

/**
 * Where `/specifications/id/:specId` lands: the spec's landing version — the
 * server's `legacyLandingVersionId`, its newest published by publish date,
 * archived included, else its newest created — or the base list for a dead id,
 * a spec with no versions, or a failed read (`summary` null; #678 Q9).
 */
export function legacySpecLandingPath(
  summary: { id: number; legacyLandingVersionId: number | null } | null,
): string {
  const landing = summary?.legacyLandingVersionId ?? null;
  return summary && landing !== null
    ? specDetailPath(summary.id, landing)
    : SPECIFICATIONS_BASE_PATH;
}

/**
 * Where `/specs/:versionId/*` lands: that version's details route under the
 * spec that owns it, the trailing path kept verbatim (`/specs/9/edit` →
 * `…/versions/9/edit`), or the base list for a dead id or a failed read
 * (`summary` null; #678 Q9).
 */
export function legacySpecsVersionPath(
  summary: { id: number } | null,
  versionId: number,
  splat: string | undefined,
): string {
  if (!summary) return SPECIFICATIONS_BASE_PATH;
  return `${specDetailPath(summary.id, versionId)}${splat ? `/${splat}` : ""}`;
}

// ---------------------------------------------------------------------------
// The public share pages (#1022; ADR-0019)
// ---------------------------------------------------------------------------
//
// Template strings with the token interpolated **unencoded**, exactly as the
// source builds them (`navigation.ts:52-54`; `public-spec-latest-redirect.tsx:
// 111`), so a link copied in either system is the same string. The token is a
// Fernet token — URL-safe base64 plus `=` padding — so there is nothing in it a
// query string would misread. Strings, not `to`: a link the source builds by
// interpolation stays one string here too, and the landing's `?reason=` is
// part of the href.

/** The public archived / link-invalid landing. */
export const PUBLIC_SPEC_ARCHIVED_PATH = "/public-spec-archived";

/**
 * The always-latest resolver, `/public-spec/id/<specId>?share_token=<token>` —
 * what the public header's Copy Link copies (`createPublicSpecLatestVersionLink`).
 * `specId` is the route's raw param, as the source copies `useParams().specId`.
 */
export function publicSpecLatestPath(specId: string, shareToken: string): string {
  return `/public-spec/id/${specId}?share_token=${shareToken}`;
}

/** The same resolver as an absolute link, for the clipboard. Browser-only. */
export function publicSpecLatestUrl(specId: string, shareToken: string): string {
  return `${window.location.origin}${publicSpecLatestPath(specId, shareToken)}`;
}

/**
 * One version's public page, carrying the token and nothing else — the
 * resolver's landing (`:111`). `specId` is the resolver's raw param.
 */
export function publicSpecVersionPath(
  specId: string,
  versionId: number,
  shareToken: string,
): string {
  return `/public-spec/${specId}/versions/${versionId}?share_token=${shareToken}`;
}

/**
 * One version's public page as an absolute link — the Spec list row's copy
 * (`createSpecificationShareLinkNonLinear`, `navigation.ts:39-41`), the token
 * interpolated unencoded as above.
 */
export function shareVersionUrl(
  origin: string,
  specId: number,
  versionId: number,
  shareToken: string,
): string {
  return `${origin}${publicSpecVersionPath(String(specId), versionId, shareToken)}`;
}

// The signed-in Share menu's two "This version" links (#1025;
// `ShareButton.tsx:93-112`) are built **from the page's own href**, exactly as
// the source builds them: `isEditRoute` is `href.includes('edit')`
// (`navigation.ts:4-7`) and each rewrite is a first-match `replace`, so a
// query value holding `/edit` loses it — a quirk kept for parity (#1025
// Assumption 8). Taking the href as an argument keeps them pure.

/** The first `/edit` gone whenever the href contains `edit` anywhere. */
function withoutEdit(href: string): string {
  return href.includes("edit") ? href.replace("/edit", "") : href;
}

/**
 * Anyone with the link × This version: the page's href with its first
 * `specifications` turned into `public-spec`, the edit face's `/edit` dropped,
 * and the minted token **set** through `URLSearchParams` — so it replaces a
 * token already there, joins with `&`, and is percent-encoded, where every
 * other share link interpolates it raw. Both spellings decode to one link.
 */
export function shareThisVersionUrl(href: string, shareToken: string): string {
  const url = new URL(withoutEdit(href.replace("specifications", "public-spec")));
  url.searchParams.set("share_token", shareToken);
  return url.toString();
}

/** Restricted × This version: the page's own address, the edit face's `/edit` dropped. */
export function restrictedThisVersionUrl(href: string): string {
  return withoutEdit(href);
}

/**
 * Restricted × Latest published: the signed-in resolver
 * (`createSpecificationLatestVersionLink`, `navigation.ts:45-47`), which lands
 * a colleague on the spec's latest version after they sign in (#694).
 */
export function restrictedSpecLatestUrl(origin: string, specId: number): string {
  return `${origin}/specifications/id/${specId}`;
}

/**
 * The spec id a public resolver's path segment names, or `null` — `Number`'s
 * own coercion, as the source (`Number(specId)`,
 * `public-spec-latest-redirect.tsx:53-56`) and the public product page read
 * theirs: `07` is spec 7, and an empty, fractional or non-numeric id asks
 * nothing and lands on the default archived page, the source's skipped-query
 * branch. Looser than `parseRouteId` on purpose: these are links from the wild,
 * and the source resolves what `Number` reads.
 */
export function parsePublicSpecId(raw: string): number | null {
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * Where a share denial lands (#551 D2): `link-invalid` on "This link is no
 * longer valid", `archived` on "This specification has been archived". The one
 * mapping the resolver uses now and the pinned page's reads (#1024) reuse.
 */
export function publicSpecLandingPath(denial: ShareDenial): string {
  return denial === "link-invalid"
    ? `${PUBLIC_SPEC_ARCHIVED_PATH}?reason=link-invalid`
    : PUBLIC_SPEC_ARCHIVED_PATH;
}

/**
 * Where a link under one of modsy's basepaths lands: the same path at the root,
 * query and hash kept verbatim (ADR-0019 decision 5) — `/digitalops` is modsy's
 * `BrowserRouter basename` (`timmy/.env.production:4`), and `/modsy-hub` its
 * pre-2024 name, which modsy's CDN still rewrites with the query kept
 * (`viewer-request-redirect/index.js:28-56`).
 *
 * `href` is the visitor's path, query and hash; `prefix` the basepath it
 * starts with.
 *
 * **Never an open redirect.** Whatever follows the prefix is a path on this
 * origin: every leading slash, backslash (which browsers read as a slash) and
 * control or space character (which the URL parser drops) collapses into one
 * `/`, so `/digitalops//evil.example` lands on `/evil.example`, never on the
 * protocol-relative `//evil.example`. A scheme or a host after it is path text.
 */
export function stripLegacyBasepath(href: string, prefix: string): string {
  const rest = href.slice(prefix.length);
  // ASCII controls and the space, which the URL parser trims or drops, sit
  // inside the class beside the two slashes.
  // eslint-disable-next-line no-control-regex
  const path = rest.replace(/^[/\\\u0000-\u0020]+/, "");
  return `/${path}`;
}
